import { Button, Card, Text, TextBadge, VSpacer } from '@/components/DesignSystem';
import { getRankCurrent, getRankEarned, getTier } from '@/pages/FarmerList/helper';
import { getHasTierGraphics } from '@/pages/CustomerDashboard/helpers';
import { TierImage } from '@/pages/CustomerDashboard/TierImage';
import { ApiFarmerRewards, ApiFarmerTierHistory, ApiRewardsProgram } from '@api/interfaces';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Stack, useTheme } from '@mui/material';
import { formatMediumDate } from '@shared/utilities';
import i18next, { TFunction } from 'i18next';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FarmerTierHistoryType } from "@shared/enums";

interface TierStatusCardProps {
  canAddOverride: boolean,
  farmerReward?: ApiFarmerRewards,
  onOpenModal: () => void,
  rewardsProgram: ApiRewardsProgram,
}

export const getHistoryEntryRank = (entry: ApiFarmerTierHistory) => (
  entry.type === FarmerTierHistoryType.Earned
    ? entry.rankEarned
    : entry.rankOverride
);

export const getTierHistoryBadgeText = (type: FarmerTierHistoryType, t: TFunction) => {
  switch (type) {
    case FarmerTierHistoryType.Override:
      return t($ => $.customers.details.tierOverride.overrideBadge);
    case FarmerTierHistoryType.Rollover:
      return t($ => $.customers.details.tierOverride.rolloverBadge);
    case FarmerTierHistoryType.Earned:
    default:
      return t($ => $.customers.details.tierOverride.earnedBadge);
  }
};

interface TierBannerProps {
  image?: ReactNode,
  isOverride: boolean,
  tierName: string,
}

export const TierBanner = ({ image, isOverride, tierName }: TierBannerProps) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'primary.main',
        borderRadius: 2,
        display: 'flex',
        gap: '12px',
        mb: '16px',
        padding: '14px 16px',
      }}
    >
      {image}
      <Box sx={{ flex: 1 }}>
        <Text category="title-medium" sx={{ color: '#fff' }}>
          {tierName}
        </Text>
        <Text category="label-small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {t($ => $.customers.details.tierOverride.currentStatus)}
        </Text>
      </Box>
      {isOverride && (
        <TextBadge
          backgroundColor="rgba(255,255,255,0.18)"
          text={t($ => $.customers.details.tierOverride.overrideBadge)}
          textCategory="overline"
          textColor="#fff"
        />
      )}
    </Box>
  );
};

export interface TierHistoryTimelineEntry {
  effectiveDate: string,
  endDate: string | null,
  id: string,
  tierName?: string,
  type: FarmerTierHistoryType,
}

export const TierHistoryTimeline = ({ entries }: { entries: TierHistoryTimelineEntry[] }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Stack>
      {entries.map((entry, index) => {
        const isLast = index === entries.length - 1;
        return (
          <Stack direction="row" key={entry.id} spacing="12px">
            <Stack alignItems="center" sx={{ width: '14px' }}>
              <Box
                sx={{
                  bgcolor: entry.endDate
                    ? theme.palette.text.disabled
                    : theme.palette.primary.main,
                  borderRadius: '50%',
                  flexShrink: 0,
                  height: '10px',
                  mt: '3px',
                  width: '10px',
                }}
              />
              {!isLast && (
                <Box
                  sx={{ bgcolor: theme.palette.outline, flex: 1, my: '4px', width: '2px' }}
                />
              )}
            </Stack>
            <Box sx={{ pb: '10px' }}>
              <Stack alignItems="center" direction="row" spacing="6px">
                <Text category="title-small">{entry.tierName}</Text>
                <TextBadge
                  backgroundColor={theme.palette.alert.warning.background}
                  text={getTierHistoryBadgeText(entry.type, t)}
                  textCategory="overline"
                  textColor="#8a5a00"
                />
              </Stack>
              <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
                {formatMediumDate(entry.effectiveDate, i18next)}
                {' – '}
                {entry.endDate
                  ? formatMediumDate(entry.endDate, i18next)
                  : t($ => $.customers.details.tierOverride.present)}
              </Text>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

export const TierStatusCard = ({
  canAddOverride,
  farmerReward,
  onOpenModal,
  rewardsProgram,
}: TierStatusCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [expanded, setExpanded] = useState(false);

  const tiers = rewardsProgram.tiers ?? [];
  const hasTierGraphics = getHasTierGraphics(tiers.map(({ name }) => name));

  const rankCurrent = getRankCurrent(farmerReward, rewardsProgram);
  const rankEarned = getRankEarned(farmerReward, rewardsProgram);
  const currentTier = rankCurrent !== null ? getTier(rankCurrent, tiers) : undefined;
  const earnedTier = rankEarned !== null ? getTier(rankEarned, tiers) : undefined;

  const tierHistory = farmerReward?.tierHistory ?? [];
  const activeTier = tierHistory.find((entry) => !entry.endDate);
  const isActiveTierAnOverride = !!tierHistory.length
    && tierHistory[tierHistory.length - 1].type === FarmerTierHistoryType.Override;

  return (
    <Card testID="tier-status-card">
      <TierBanner
        image={!!currentTier && (
          <TierImage
            hasTierGraphics={hasTierGraphics}
            size={{
              height: '30px',
              width: 'auto',
            }}
            tier={currentTier}
          />
        )}
        isOverride={isActiveTierAnOverride}
        tierName={currentTier ? currentTier.name : t($ => $.customers.details.notEnrolled)}
      />
      <Stack direction="row" spacing="16px">
        <Box sx={{ flex: 1 }}>
          <Text category="overline" sx={{ color: theme.palette.text.secondary }}>
            {t($ => $.customers.details.tierOverride.currentTierLabel)}
          </Text>
          <VSpacer size="1" />
          <Text category="label-large">
            {earnedTier ? earnedTier.name : t($ => $.customers.details.notEnrolled)}
          </Text>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Text category="overline" sx={{ color: theme.palette.text.secondary }}>
            {t($ => $.customers.details.tierOverride.asOf)}
          </Text>
          <VSpacer size="1" />
          <Text category="label-large">
            {activeTier ? formatMediumDate(activeTier.effectiveDate, i18next) : '—'}
          </Text>
        </Box>
      </Stack>
      <VSpacer size="4" />
      <Box sx={{ borderTop: `1px solid ${theme.palette.outline}` }} />
      <VSpacer size="4" />
      <Stack direction="row" spacing="8px">
        {tierHistory.length > 0 && (
          <Button
            color="secondary"
            endIcon={(
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms',
                }}
              />
            )}
            onClick={() => setExpanded(!expanded)}
            startIcon={<CalendarTodayOutlinedIcon />}
            sx={{ flex: 1, minWidth: 0 }}
            testID="tier-status-history-toggle"
            variant="outlined"
          >
            {t($ => $.customers.details.tierOverride.history)}
          </Button>
        )}
        {canAddOverride && (
          <Button
            onClick={onOpenModal}
            startIcon={<EditOutlinedIcon />}
            sx={{ flex: 1, minWidth: 0 }}
            testID="tier-status-add-override-button"
            variant="outlined"
          >
            {t($ => $.customers.details.tierOverride.addOverride)}
          </Button>
        )}
      </Stack>
      {expanded && tierHistory.length > 0 && (
        <>
          <VSpacer size="7" />
          <TierHistoryTimeline
            entries={tierHistory.map((entry) => ({
              effectiveDate: entry.effectiveDate,
              endDate: entry.endDate,
              id: entry.id,
              tierName: getTier(getHistoryEntryRank(entry) ?? -1, tiers)?.name,
              type: entry.type,
            }))}
          />
        </>
      )}
    </Card>
  );
};
